import React, { useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    IconButton,
    Typography,
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import {
    PendingSpot,
    approvePendingSpot,
    clearSelectedSpot,
    fetchPendingSpots,
    fetchSpotDetail,
} from 'store/slices/pendingSpots';

interface PendingSpotsPanelProps {
    open: boolean;
    onClose: () => void;
}

const SpotDetailDialog = ({
    open,
    onClose,
    onApprove,
    approving,
}: {
    open: boolean;
    onClose: () => void;
    onApprove: () => void;
    approving: boolean;
}) => {
    const { selectedSpot, loadingDetail } = useAppSelector((state) => state.pendingSpots);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <PlaceIcon sx={{ color: 'error.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" noWrap fontWeight={700}>
                        {selectedSpot?.name || 'Spot details'}
                    </Typography>
                    {selectedSpot?.dateAdded && (
                        <Typography variant="caption" color="text.secondary">
                            Submitted {new Date(selectedSpot.dateAdded).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loadingDetail ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : selectedSpot ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Privacy / Anonymity chips */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                                label={selectedSpot.privacy === 'PRIVACY_PUBLIC' ? 'Public' : 'Private'}
                                size="small"
                                sx={{ backgroundColor: 'error.main', color: 'error.contrastText', fontWeight: 700 }}
                            />
                            {selectedSpot.anonymity && (
                                <Chip
                                    label={selectedSpot.anonymity === 'ANONYMITY_ON' ? 'Anonymous' : 'Named'}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        {/* Location */}
                        {selectedSpot.lat !== undefined && selectedSpot.lng !== undefined && (
                            <Box>
                                <Typography variant="caption" color="text.disabled" display="block">
                                    {selectedSpot.lat.toFixed(6)}, {selectedSpot.lng.toFixed(6)}
                                </Typography>
                                <a
                                    href={`https://www.google.com/maps?q=${selectedSpot.lat},${selectedSpot.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                >
                                    Open in Google Maps <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                                </a>
                            </Box>
                        )}

                        <Divider />

                        {/* Description */}
                        {selectedSpot.description && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                                    DESCRIPTION
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedSpot.description}
                                </Typography>
                            </Box>
                        )}

                        {/* Photos */}
                        {selectedSpot.pictures && selectedSpot.pictures.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                                    PHOTOS ({selectedSpot.pictures.length})
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {selectedSpot.pictures.map((pic, i) => (
                                        <Box
                                            key={i}
                                            component="img"
                                            src={pic.mediumUrl ?? pic.smallUrl}
                                            alt={`spot photo ${i + 1}`}
                                            sx={{
                                                width: 120,
                                                height: 90,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => window.open(pic.largeUrl ?? pic.mediumUrl, '_blank')}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* YouTube */}
                        {selectedSpot.youTubeId && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                                    VIDEO
                                </Typography>
                                <a
                                    href={`https://www.youtube.com/watch?v=${selectedSpot.youTubeId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.875rem' }}
                                >
                                    <YouTubeIcon sx={{ color: '#FF0000' }} fontSize="small" />
                                    Watch on YouTube
                                </a>
                            </Box>
                        )}

                        {/* Categories */}
                        {selectedSpot.categories && selectedSpot.categories.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                                    CATEGORIES
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {selectedSpot.categories.map((c) => (
                                        <Chip key={c.id} label={c.name} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Hazards */}
                        {selectedSpot.hazards && selectedSpot.hazards.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                                    HAZARDS
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {selectedSpot.hazards.map((h) => (
                                        <Chip key={h.id} label={h.name} size="small" color="warning" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            {!loadingDetail && selectedSpot && (
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="success"
                        endIcon={approving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                        onClick={() => { onApprove(); onClose(); }}
                        disabled={approving}
                    >
                        Approve
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

const PendingSpotCard = ({
    spot,
    approving,
    onApprove,
    onViewDetails,
}: {
    spot: PendingSpot;
    approving: boolean;
    onApprove: () => void;
    onViewDetails: () => void;
}) => (
    <Box
        sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            backgroundColor: 'background.paper',
        }}
    >
        {/* Left: icon + name + description */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlaceIcon sx={{ color: 'error.main', flexShrink: 0 }} fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                        {spot.name || 'Unnamed spot'}
                    </Typography>
                    {spot.dateAdded && (
                        <Typography variant="caption" color="text.secondary">
                            Submitted {new Date(spot.dateAdded).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                    )}
                </Box>
                <Chip
                    label={spot.privacy === 'PRIVACY_PUBLIC' ? 'Public' : 'Private'}
                    size="small"
                    sx={{
                        ml: 'auto',
                        flexShrink: 0,
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                    }}
                />
            </Box>
            {spot.description && (
                <Typography variant="caption" color="text.secondary" sx={{
                    display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}
                >
                    {spot.description}
                </Typography>
            )}
            {spot.lat !== undefined && spot.lng !== undefined && (
                <Typography variant="caption" color="text.disabled">
                    {spot.lat.toFixed(5)}, {spot.lng.toFixed(5)}
                </Typography>
            )}
        </Box>

        {/* Right: buttons */}
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
                size="small"
                variant="outlined"
                onClick={onViewDetails}
            >
                View details
            </Button>
            <Button
                size="small"
                variant="contained"
                color="success"
                endIcon={approving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                onClick={onApprove}
                disabled={approving}
            >
                Approve
            </Button>
        </Box>
    </Box>
);

const PendingSpotsPanel = ({ open, onClose }: PendingSpotsPanelProps) => {
    const dispatch = useAppDispatch();
    const { spots, loading, approvingId, selectedSpot, loadingDetail } = useAppSelector((state) => state.pendingSpots);
    const detailOpen = !!(selectedSpot || loadingDetail);

    useEffect(() => {
        if (open) {
            void dispatch(fetchPendingSpots());
        }
    }, [open, dispatch]);

    const handleViewDetails = (id: string) => {
        void dispatch(fetchSpotDetail(id));
    };

    const handleCloseDetail = () => {
        dispatch(clearSelectedSpot());
    };

    return (
        <>
            <Drawer anchor="bottom" open={open} onClose={onClose}>
                {/* Drag handle */}
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
                    <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'divider' }} />
                </Box>

                <Box sx={{ px: 2, pb: 3, maxHeight: '70vh', overflowY: 'auto' }}>
                    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PendingActionsIcon sx={{ color: 'error.main' }} />
                            <Typography variant="h6" fontWeight={700}>
                                Pending approval
                            </Typography>
                            <Chip
                                label={spots.length}
                                size="small"
                                sx={{ backgroundColor: 'error.main', color: 'error.contrastText', fontWeight: 700, height: 20 }}
                            />
                        </Box>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : spots.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No spots pending approval.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {spots.map((s) => (
                                    <PendingSpotCard
                                        key={s.id}
                                        spot={s}
                                        approving={approvingId === s.id}
                                        onApprove={() => s.id && void dispatch(approvePendingSpot(s.id))}
                                        onViewDetails={() => s.id && handleViewDetails(s.id)}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <SpotDetailDialog
                open={detailOpen}
                onClose={handleCloseDetail}
                onApprove={() => selectedSpot?.id && void dispatch(approvePendingSpot(selectedSpot.id))}
                approving={!!(selectedSpot?.id && approvingId === selectedSpot.id)}
            />
        </>
    );
};

export default PendingSpotsPanel;
